import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Puzzle } from '../types';
import { puzzleAPI } from '../api';
import { formatDate } from '../utils/format';
import {
  colors,
  fonts,
  radius,
  shadow,
  sectionLabel,
  focusRing,
} from '../theme';

const ArchiveContainer = styled.div`
  padding: 36px 20px 20px;
  max-width: 760px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-size: 30px;
  text-align: center;
  margin: 0 0 24px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${colors.rule};
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 22px;
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  background: none;
  border: none;
  padding: 0 2px 11px;
  font-size: 14px;
  font-weight: ${props => (props.$active ? 700 : 500)};
  color: ${props => (props.$active ? colors.ink : colors.inkFaint)};
  cursor: pointer;
  ${focusRing}

  &:hover {
    color: ${colors.ink};
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: ${props => (props.$active ? colors.accent : 'transparent')};
  }
`;

const SearchInput = styled.input`
  margin-bottom: 10px;
  padding: 7px 11px;
  min-width: 220px;
  border: 1px solid ${colors.ruleStrong};
  border-radius: ${radius.sm};
  background: ${colors.card};
  color: ${colors.ink};
  font-size: 14px;

  &::placeholder {
    color: ${colors.inkFaint};
  }

  &:focus {
    outline: none;
    border-color: ${colors.accent};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${colors.card};
  border: 1px solid ${colors.rule};
  border-radius: ${radius.md};
  box-shadow: ${shadow.panel};
  overflow: hidden;
`;

const TableHeader = styled.th`
  ${sectionLabel}
  background: ${colors.cardMuted};
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid ${colors.rule};

  &:last-child {
    text-align: right;
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid ${colors.rule};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.cardMuted};
  }
`;

const TableCell = styled.td`
  padding: 10px 14px;
  font-size: 14px;
  color: ${colors.ink};

  &:last-child {
    text-align: right;
    color: ${colors.inkFaint};
    font-size: 13px;
    white-space: nowrap;
  }
`;

/* Outlined tag rather than a filled pill — quieter next to the row text. */
const Tag = styled.span`
  ${sectionLabel}
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid ${colors.accent};
  border-radius: ${radius.sm};
  color: ${colors.accent};
`;

const Message = styled.div`
  text-align: center;
  padding: 56px 20px;
  font-family: ${fonts.display};
  font-size: 16px;
  color: ${colors.inkFaint};
`;

export const Archive: React.FC = () => {
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [dailyPuzzles, setDailyPuzzles] = useState<Puzzle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'daily'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const loadPuzzles = async () => {
      try {
        const [allPuzzles, dailyArchive] = await Promise.all([
          puzzleAPI.getPuzzles(),
          puzzleAPI.getDailyPuzzleArchive(0, 100),
        ]);
        setPuzzles(
          [...allPuzzles].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
        );
        setDailyPuzzles(
          [...dailyArchive].sort(
            (a, b) =>
              new Date(b.daily_date || b.created_at).getTime() -
              new Date(a.daily_date || a.created_at).getTime()
          )
        );
      } catch (error) {
        console.error('Error loading puzzles:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPuzzles();
  }, []);

  const filteredPuzzles = useMemo(() => {
    const source = viewMode === 'daily' ? dailyPuzzles : puzzles;
    if (!searchTerm) return source;

    const needle = searchTerm.toLowerCase();
    return source.filter(puzzle => puzzle.title.toLowerCase().includes(needle));
  }, [puzzles, dailyPuzzles, searchTerm, viewMode]);

  if (loading) {
    return <Message>Loading archive…</Message>;
  }

  return (
    <ArchiveContainer>
      <Title>Archive</Title>

      <Toolbar>
        <Tabs>
          <Tab $active={viewMode === 'all'} onClick={() => setViewMode('all')}>
            All ({puzzles.length})
          </Tab>
          <Tab $active={viewMode === 'daily'} onClick={() => setViewMode('daily')}>
            Daily ({dailyPuzzles.length})
          </Tab>
        </Tabs>

        <SearchInput
          type="search"
          placeholder="Search by title…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          aria-label="Search puzzles by title"
        />
      </Toolbar>

      {filteredPuzzles.length === 0 ? (
        <Message>
          {searchTerm
            ? 'Nothing matches that search.'
            : viewMode === 'daily'
            ? 'No daily puzzles yet.'
            : 'No puzzles in the archive yet.'}
        </Message>
      ) : (
        <Table>
          <thead>
            <tr>
              <TableHeader>Puzzle</TableHeader>
              <TableHeader>Date</TableHeader>
            </tr>
          </thead>
          <tbody>
            {filteredPuzzles.map(puzzle => (
              <TableRow key={puzzle.id} onClick={() => navigate(`/puzzle/${puzzle.id}`)}>
                <TableCell>
                  {puzzle.title} {puzzle.is_daily && <Tag>daily</Tag>}
                </TableCell>
                <TableCell>
                  {formatDate(puzzle.daily_date || puzzle.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}
    </ArchiveContainer>
  );
};

export default Archive;
